/**
 * ISOLATION FILE — Polymorphic SlotFunctionArguments engine
 *
 * Generic, component-agnostic namespace. Owns:
 *   1. All slot bucket definitions and the type → bucket composition mapping (engine internals)
 *   2. Generic engine exports (SlotFunctionArguments<T, TOverrides>, PolymorphicSlotFunctionArguments<T, TOverrides>)
 *
 * Does NOT own component-specific types (CalendarDaySlotArguments, slot overrides).
 * Those live in DSControlledDateTimePickerT, which uses this engine's generic exports
 * and provides its own TOverrides to create its Slots<T> and PolymorphicSlots<T>.
 */

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace ControlledDateTimePickerSlotEngine {
  // ---------------------------------------------------------------------------
  // ControlTypes
  // ---------------------------------------------------------------------------

  export type ControlTypes =
    | 'full-date-time'
    | 'date-time-inputs'
    | 'date-time-picker'
    | 'date-time-picker-controller-only'
    | 'full-date'
    | 'date-inputs'
    | 'date-picker'
    | 'date-picker-controller-only'
    | 'full-time'
    | 'time-inputs'
    | 'time-picker'
    | 'time-picker-controller-only';

  // ---------------------------------------------------------------------------
  // Slot Buckets (internal — not exported)
  //
  // All slot values are () => object. Typed argument overrides are injected
  // externally via TOverrides — the engine internals are argument-type-agnostic.
  //
  // No slot key appears in more than one bucket. Buckets compose additively.
  //
  //   Root        — always present
  //   ClearBtn    — input types only, gated by isClearable at runtime
  //   DateInputs  — month/day/year text inputs
  //   TimeInputs  — hour/minute/meridiem text inputs
  //
  //   Calendar family (calendar-only types):
  //     CalendarContent      — inner calendar UI, picker AND controller-only
  //     CalendarFooter       — footer message, picker AND controller-only
  //     CalendarPickerWrapper — trigger icon + popper, picker only
  //
  //   Timewheel family (timewheel-only types):
  //     TimewheelContent      — inner timewheel UI, picker AND controller-only
  //     TimewheelFooter       — footer message, picker AND controller-only
  //     TimewheelPickerWrapper — trigger icon + popper, picker only
  //
  //   Combined family (CalendarWithTimeWheel — both pickers together):
  //     CalendarContent + TimewheelContent  (reused from above)
  //     CalendarTimewheelContent            — combined controller wrapper, picker AND controller-only
  //     CalendarTimewheelFooter             — combined footer, picker AND controller-only
  //     CalendarTimewheelPickerWrapper      — combined trigger icon + popper, picker only
  // ---------------------------------------------------------------------------

  type SlotsBucketRoot = {
    dsControlleddatetimepickerRoot: () => object;
  };

  type SlotsBucketClearBtn = {
    dsControlleddatetimepickerClearBtn: () => object;
  };

  type SlotsBucketDateInputs = {
    dsControlleddatetimepickerTimePickerMonthInput: () => object;
    dsControlleddatetimepickerTimePickerDayInput: () => object;
    dsControlleddatetimepickerTimePickerYearInput: () => object;
  };

  type SlotsBucketTimeInputs = {
    dsControlleddatetimepickerTimePickerHourInput: () => object;
    dsControlleddatetimepickerTimePickerMinuteInput: () => object;
    dsControlleddatetimepickerTimePickerMeridiemInput: () => object;
  };

  type SlotsBucketCalendarContent = {
    dsControlleddatetimepickerCalendarDay: () => object;
    dsControlleddatetimepickerCalendarHeaderLabel: () => object;
    dsControlleddatetimepickerCalendarHeaderButtons: () => object;
    dsControlleddatetimepickerCalendarHeaderWeeklyDayList: () => object;
    dsControlleddatetimepickerCalendarControllerWrapper: () => object;
  };

  type SlotsBucketCalendarFooter = {
    dsControlleddatetimepickerCalendarContextFooter: () => object;
  };

  type SlotsBucketCalendarPickerWrapper = {
    dsControlleddatetimepickerCalendarTriggerIcon: () => object;
    dsControlleddatetimepickerCalendarContextContentWrapper: () => object;
  };

  type SlotsBucketTimewheelContent = {
    dsControlleddatetimepickerTimewheelTimeButton: () => object;
    dsControlleddatetimepickerTimewheelTimeChangeButton: () => object;
    dsControlleddatetimepickerTimewheelHeaderLabel: () => object;
    dsControlleddatetimepickerTimewheelControllerWrapper: () => object;
  };

  type SlotsBucketTimewheelFooter = {
    dsControlleddatetimepickerTimewheelContextFooter: () => object;
  };

  type SlotsBucketTimewheelPickerWrapper = {
    dsControlleddatetimepickerTimewheelTriggerIcon: () => object;
    dsControlleddatetimepickerTimewheelContextContentWrapper: () => object;
  };

  type SlotsBucketCalendarTimewheelContent = {
    dsControlleddatetimepickerCalendarTimewheelControllerWrapper: () => object;
  };

  type SlotsBucketCalendarTimewheelFooter = {
    dsControlleddatetimepickerCalendarTimewheelContextFooter: () => object;
  };

  type SlotsBucketCalendarTimewheelPickerWrapper = {
    dsControlleddatetimepickerCalendarTimewheelTriggerIcon: () => object;
    dsControlleddatetimepickerCalendarTimewheelContextContentWrapper: () => object;
  };

  // ---------------------------------------------------------------------------
  // Engine internals (not exported)
  // ---------------------------------------------------------------------------

  type AllSlotsBase = SlotsBucketRoot &
    SlotsBucketClearBtn &
    SlotsBucketDateInputs &
    SlotsBucketTimeInputs &
    SlotsBucketCalendarContent &
    SlotsBucketCalendarFooter &
    SlotsBucketCalendarPickerWrapper &
    SlotsBucketTimewheelContent &
    SlotsBucketTimewheelFooter &
    SlotsBucketTimewheelPickerWrapper &
    SlotsBucketCalendarTimewheelContent &
    SlotsBucketCalendarTimewheelFooter &
    SlotsBucketCalendarTimewheelPickerWrapper;

  // eslint-disable-next-line @typescript-eslint/ban-types
  export type SlotOverrides = Partial<{ [K in keyof AllSlotsBase]: Function }>;

  type ApplyOverrides<TBuckets, TOverrides extends SlotOverrides> = {
    [K in keyof TBuckets]: K extends keyof TOverrides ? NonNullable<TOverrides[K]> : TBuckets[K];
  };

  // prettier-ignore
  type ComposedBucketsFor<T extends ControlTypes> =
    T extends 'full-date-time'
      ? SlotsBucketRoot & SlotsBucketDateInputs & SlotsBucketTimeInputs & SlotsBucketClearBtn
        & SlotsBucketCalendarContent & SlotsBucketTimewheelContent & SlotsBucketCalendarTimewheelContent
        & SlotsBucketCalendarTimewheelFooter & SlotsBucketCalendarTimewheelPickerWrapper
    : T extends 'date-time-inputs'
      ? SlotsBucketRoot & SlotsBucketDateInputs & SlotsBucketTimeInputs & SlotsBucketClearBtn
    : T extends 'date-time-picker'
      ? SlotsBucketRoot & SlotsBucketCalendarContent & SlotsBucketTimewheelContent & SlotsBucketCalendarTimewheelContent
        & SlotsBucketCalendarTimewheelFooter & SlotsBucketCalendarTimewheelPickerWrapper
    : T extends 'date-time-picker-controller-only'
      ? SlotsBucketRoot & SlotsBucketCalendarContent & SlotsBucketTimewheelContent & SlotsBucketCalendarTimewheelContent
        & SlotsBucketCalendarTimewheelFooter
    : T extends 'full-date'
      ? SlotsBucketRoot & SlotsBucketDateInputs & SlotsBucketClearBtn
        & SlotsBucketCalendarContent & SlotsBucketCalendarFooter & SlotsBucketCalendarPickerWrapper
    : T extends 'date-inputs'
      ? SlotsBucketRoot & SlotsBucketDateInputs & SlotsBucketClearBtn
    : T extends 'date-picker'
      ? SlotsBucketRoot & SlotsBucketCalendarContent & SlotsBucketCalendarFooter & SlotsBucketCalendarPickerWrapper
    : T extends 'date-picker-controller-only'
      ? SlotsBucketRoot & SlotsBucketCalendarContent & SlotsBucketCalendarFooter
    : T extends 'full-time'
      ? SlotsBucketRoot & SlotsBucketTimeInputs & SlotsBucketClearBtn
        & SlotsBucketTimewheelContent & SlotsBucketTimewheelFooter & SlotsBucketTimewheelPickerWrapper
    : T extends 'time-inputs'
      ? SlotsBucketRoot & SlotsBucketTimeInputs & SlotsBucketClearBtn
    : T extends 'time-picker'
      ? SlotsBucketRoot & SlotsBucketTimewheelContent & SlotsBucketTimewheelFooter & SlotsBucketTimewheelPickerWrapper
    : T extends 'time-picker-controller-only'
      ? SlotsBucketRoot & SlotsBucketTimewheelContent & SlotsBucketTimewheelFooter
    : never;

  // ---------------------------------------------------------------------------
  // Generic engine exports
  // Available for any consumer that needs a different override set.
  // ---------------------------------------------------------------------------

  export type SlotFunctionArguments<
    T extends ControlTypes,
    TOverrides extends SlotOverrides = Record<string, unknown>,
  > = ApplyOverrides<ComposedBucketsFor<T>, TOverrides>;

  export type PolymorphicSlotFunctionArguments<
    T extends ControlTypes,
    TOverrides extends SlotOverrides = Record<string, unknown>,
  > = {
    [K in keyof SlotFunctionArguments<T, TOverrides>]?: object | SlotFunctionArguments<T, TOverrides>[K];
  };
}

// Engine structural contract tests live in:
//   src/typescript-testing/slot-arguments-engine.ts
